<template>
  <div class="checkin-container">
    <div class="checkin-card">
      <!-- Header -->
      <div class="checkin-header">
        <i class="el-icon-location-information"></i>
        <h1>Check-In Event</h1>
        <p>Silakan isi form di bawah untuk melakukan check-in</p>
      </div>

      <!-- Loading Event Info -->
      <div v-if="loadingEvent" class="loading-section">
        <el-skeleton :rows="5" animated />
      </div>

      <!-- Event Info -->
      <el-card v-if="eventInfo && !loadingEvent" class="event-info-card" shadow="hover">
        <div class="event-details">
          <h2>{{ eventInfo.subject }}</h2>
          <div class="info-row">
            <i class="el-icon-calendar"></i>
            <span>{{ formatDate(eventInfo.startDateTime) }}</span>
          </div>
          <div class="info-row">
            <i class="el-icon-time"></i>
            <span>{{ formatTime(eventInfo.startDateTime) }} - {{ formatTime(eventInfo.endDateTime) }}</span>
          </div>
          <div class="info-row" v-if="eventInfo.location">
            <i class="el-icon-map-location"></i>
            <span>{{ eventInfo.location }}</span>
          </div>
          <div class="info-row">
            <i class="el-icon-user"></i>
            <span>Organizer: {{ eventInfo.organizerName }}</span>
          </div>
        </div>
      </el-card>

      <!-- Error Message -->
      <el-alert
        v-if="errorMessage && !loadingEvent"
        :title="errorMessage"
        type="error"
        :closable="false"
        show-icon
        style="margin: 20px 0;"
      />

      <!-- Check-In Form -->
      <el-form
        v-if="!checkInSuccess && eventInfo && !loadingEvent"
        :model="checkInForm"
        :rules="checkInRules"
        ref="checkInForm"
        label-position="top"
        class="checkin-form"
      >
        <el-form-item label="Nama Lengkap" prop="attendeeName">
          <el-input
            v-model="checkInForm.attendeeName"
            placeholder="Masukkan nama lengkap Anda"
            prefix-icon="el-icon-user"
            size="large"
          />
        </el-form-item>

        <el-form-item label="Email" prop="attendeeEmail">
          <el-input
            v-model="checkInForm.attendeeEmail"
            placeholder="Masukkan email Anda"
            prefix-icon="el-icon-message"
            size="large"
            type="email"
          />
          <div class="form-hint">Email harus sesuai dengan email yang terdaftar sebagai peserta</div>
        </el-form-item>

        <el-form-item label="Nomor Telepon (Opsional)" prop="phoneNumber">
          <el-input
            v-model="checkInForm.phoneNumber"
            placeholder="Masukkan nomor telepon"
            prefix-icon="el-icon-phone"
            size="large"
          />
        </el-form-item>

        <!-- Location Capture (if supported) -->
        <el-form-item v-if="locationSupported">
          <el-checkbox v-model="captureLocation">
            Izinkan akses lokasi untuk verifikasi kehadiran
          </el-checkbox>
          <div class="form-hint">
            <i class="el-icon-info"></i>
            Lokasi Anda akan digunakan untuk memverifikasi kehadiran di tempat event
          </div>
        </el-form-item>

        <el-button
          type="primary"
          size="large"
          @click="handleCheckIn"
          :loading="submitting"
          class="checkin-button"
          block
        >
          <i class="el-icon-circle-check"></i>
          Check-In Sekarang
        </el-button>
      </el-form>

      <!-- Success Message -->
      <div v-if="checkInSuccess" class="success-section">
        <div class="success-icon">
          <i class="el-icon-success"></i>
        </div>
        <h2>Check-In Berhasil!</h2>
        <p>Terima kasih telah melakukan check-in.</p>
        <p class="success-detail">{{ checkInResponse?.message }}</p>
        
        <div v-if="checkInResponse" class="checkin-details">
          <div class="detail-item">
            <span class="label">Waktu Check-In:</span>
            <span class="value">{{ formatCheckInTime(checkInResponse.checkInTime) }}</span>
          </div>
          <div class="detail-item" v-if="checkInResponse.attendeeName">
            <span class="label">Nama:</span>
            <span class="value">{{ checkInResponse.attendeeName }}</span>
          </div>
          <div class="detail-item" v-if="checkInResponse.status">
            <span class="label">Status:</span>
            <el-tag :type="checkInResponse.status === 'SUCCESS' ? 'success' : 'warning'" size="small">
              {{ checkInResponse.status }}
            </el-tag>
          </div>
        </div>

        <el-button
          type="default"
          size="medium"
          @click="resetForm"
          style="margin-top: 20px;"
        >
          Check-In Peserta Lain
        </el-button>
      </div>
    </div>

    <!-- Footer -->
    <div class="checkin-footer">
      <p>Powered by Intranet LAN</p>
      <p>© 2025 Lembaga Administrasi Negara</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'EventCheckIn',
  data() {
    return {
      eventId: '',
      organizerEmail: '',
      loadingEvent: false,
      submitting: false,
      eventInfo: null,
      errorMessage: '',
      checkInSuccess: false,
      checkInResponse: null,
      locationSupported: false,
      captureLocation: false,
      userLocation: null,
      checkInForm: {
        attendeeName: '',
        attendeeEmail: '',
        phoneNumber: ''
      },
      checkInRules: {
        attendeeName: [
          { required: true, message: 'Nama lengkap wajib diisi', trigger: 'blur' },
          { min: 3, message: 'Nama minimal 3 karakter', trigger: 'blur' }
        ],
        attendeeEmail: [
          { required: true, message: 'Email wajib diisi', trigger: 'blur' },
          { type: 'email', message: 'Format email tidak valid', trigger: 'blur' }
        ]
      }
    };
  },
  mounted() {
    // Get eventId and organizer from URL query params
    this.eventId = this.$route.query.eventId || this.$route.params.eventId;
    this.organizerEmail = this.$route.query.organizer || this.$route.query.email;

    if (!this.eventId) {
      this.errorMessage = 'Event ID tidak ditemukan. Link tidak valid.';
      return;
    }

    // Check if geolocation is supported
    if ('geolocation' in navigator) {
      this.locationSupported = true;
    }

    // Load event info
    this.loadEventInfo();

    // Pre-fill email if provided in URL
    if (this.$route.query.attendeeEmail) {
      this.checkInForm.attendeeEmail = this.$route.query.attendeeEmail;
    }
  },
  methods: {
    ...mapActions('integration', ['getEventDetails', 'submitCheckIn']),

    async loadEventInfo() {
      if (!this.eventId || !this.organizerEmail) {
        this.errorMessage = 'Informasi event tidak lengkap. Link tidak valid.';
        return;
      }

      this.loadingEvent = true;
      this.errorMessage = '';

      try {
        // Fetch event details from API
        const eventData = await this.getEventDetails({
          userIdOrUpn: this.organizerEmail,
          eventId: this.eventId
        });

        this.eventInfo = {
          subject: eventData.subject || 'Untitled Event',
          startDateTime: eventData.start?.dateTime || eventData.startDateTime,
          endDateTime: eventData.end?.dateTime || eventData.endDateTime,
          location: eventData.locationName || eventData.location || '-',
          organizerName: eventData.organizer?.name || 'Unknown',
          organizerEmail: eventData.organizer?.email || this.organizerEmail
        };
      } catch (error) {
        console.error('Error loading event info:', error);
        this.errorMessage = 'Gagal memuat informasi event: ' + (error.message || 'Unknown error');
      } finally {
        this.loadingEvent = false;
      }
    },

    async handleCheckIn() {
      this.$refs.checkInForm.validate(async (valid) => {
        if (!valid) {
          this.$message.error('Mohon lengkapi form dengan benar');
          return;
        }

        this.submitting = true;

        try {
          // Get location if permitted
          if (this.captureLocation && this.locationSupported) {
            await this.getUserLocation();
          }

          // Prepare check-in request
          const checkInRequest = {
            eventId: this.eventId,
            organizerEmail: this.organizerEmail,
            attendeeEmail: this.checkInForm.attendeeEmail,
            attendeeName: this.checkInForm.attendeeName,
            phoneNumber: this.checkInForm.phoneNumber || null,
            latitude: this.userLocation?.latitude || null,
            longitude: this.userLocation?.longitude || null,
            deviceInfo: this.getDeviceInfo()
          };

          console.log('Submitting check-in:', checkInRequest);

          // Submit check-in
          const response = await this.submitCheckIn(checkInRequest);

          this.checkInSuccess = true;
          this.checkInResponse = response;

          this.$message.success('Check-in berhasil!');
        } catch (error) {
          console.error('Check-in error:', error);
          
          // Handle specific error messages
          let errorMsg = 'Gagal melakukan check-in';
          if (error.response?.data?.message) {
            errorMsg = error.response.data.message;
          } else if (error.message) {
            errorMsg = error.message;
          }

          this.$message.error(errorMsg);
        } finally {
          this.submitting = false;
        }
      });
    },

    async getUserLocation() {
      return new Promise((resolve, reject) => {
        if (!this.locationSupported) {
          resolve();
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.userLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };
            resolve();
          },
          (error) => {
            console.warn('Location access denied:', error);
            this.$message.warning('Tidak dapat mengakses lokasi. Check-in akan dilanjutkan tanpa verifikasi lokasi.');
            resolve();
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });
    },

    getDeviceInfo() {
      return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      };
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return dateString;
      }
    },

    formatTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('id-ID', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },

    formatCheckInTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('id-ID', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },

    resetForm() {
      this.checkInSuccess = false;
      this.checkInResponse = null;
      this.checkInForm = {
        attendeeName: '',
        attendeeEmail: '',
        phoneNumber: ''
      };
      this.userLocation = null;
      this.captureLocation = false;
      if (this.$refs.checkInForm) {
        this.$refs.checkInForm.clearValidate();
      }
    }
  }
};
</script>

<style scoped>
.checkin-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.checkin-card {
  width: 100%;
  max-width: 600px;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.checkin-header {
  text-align: center;
  margin-bottom: 30px;
}

.checkin-header i {
  font-size: 64px;
  color: #667eea;
  margin-bottom: 15px;
}

.checkin-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 10px 0;
}

.checkin-header p {
  color: #606266;
  font-size: 16px;
  margin: 5px 0;
}

.loading-section {
  padding: 20px 0;
}

.event-info-card {
  margin-bottom: 30px;
  border-radius: 12px;
}

.event-details h2 {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  align-items: center;
  margin: 10px 0;
  color: #606266;
  font-size: 15px;
}

.info-row i {
  font-size: 18px;
  margin-right: 10px;
  color: #667eea;
  width: 20px;
}

.checkin-form {
  margin-top: 20px;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.5;
}

.form-hint i {
  margin-right: 3px;
}

.checkin-button {
  margin-top: 20px;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.checkin-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.success-section {
  text-align: center;
  padding: 30px 0;
}

.success-icon {
  margin-bottom: 20px;
}

.success-icon i {
  font-size: 80px;
  color: #67C23A;
  animation: scaleIn 0.6s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.success-section h2 {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 15px 0;
}

.success-section p {
  color: #606266;
  font-size: 16px;
  margin: 10px 0;
}

.success-detail {
  color: #67C23A !important;
  font-weight: 600;
}

.checkin-details {
  margin: 25px auto;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 12px;
  max-width: 400px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e4e7ed;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  font-weight: 600;
  color: #606266;
}

.detail-item .value {
  color: #2c3e50;
}

.checkin-footer {
  text-align: center;
  margin-top: 30px;
  color: white;
  font-size: 14px;
}

.checkin-footer p {
  margin: 5px 0;
  opacity: 0.9;
}

@media (max-width: 768px) {
  .checkin-card {
    padding: 25px;
  }

  .checkin-header h1 {
    font-size: 24px;
  }

  .checkin-header i {
    font-size: 48px;
  }
}
</style>
